/**
 * Sistema de Cache Inteligente
 * Implementa cache em múltiplas camadas com invalidação automática
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  tags: string[];
}

interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
  enableCompression: boolean;
  enablePersistence: boolean;
}

export class IntelligentCache {
  private static instance: IntelligentCache;
  private memoryCache = new Map<string, CacheItem<any>>();
  private config: CacheConfig;
  private cleanupInterval?: NodeJS.Timeout;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutos
      maxSize: 1000,
      enableCompression: true,
      enablePersistence: true,
      ...config
    };

    this.startCleanupProcess();
    this.loadFromPersistence();
  }

  static getInstance(config?: Partial<CacheConfig>): IntelligentCache {
    if (!IntelligentCache.instance) {
      IntelligentCache.instance = new IntelligentCache(config);
    }
    return IntelligentCache.instance;
  }

  // Armazenar no cache
  set<T>(
    key: string, 
    data: T, 
    options: {
      ttl?: number;
      tags?: string[];
      priority?: 'low' | 'normal' | 'high';
    } = {}
  ): void {
    const {
      ttl = this.config.defaultTTL,
      tags = [],
      priority = 'normal'
    } = options;

    // Verificar limite de tamanho
    if (this.memoryCache.size >= this.config.maxSize) {
      this.evictLeastUsed();
    }

    const item: CacheItem<T> = {
      data: this.config.enableCompression ? this.compress(data) : data,
      timestamp: Date.now(),
      ttl,
      accessCount: 0,
      lastAccessed: Date.now(),
      tags: [...tags, `priority:${priority}`]
    };

    this.memoryCache.set(key, item);
    
    // Persistir se habilitado
    if (this.config.enablePersistence) {
      this.persistToStorage(key, item);
    }

    console.log(`📦 Cache SET: ${key} (TTL: ${ttl}ms, Tags: ${tags.join(', ')})`);
  }

  // Recuperar do cache
  get<T>(key: string): T | null {
    const item = this.memoryCache.get(key);
    
    if (!item) {
      // Tentar carregar da persistência
      const persistedItem = this.loadFromStorage(key);
      if (persistedItem && !this.isExpired(persistedItem)) {
        this.memoryCache.set(key, persistedItem);
        return this.processGetResult(key, persistedItem);
      }
      return null;
    }

    if (this.isExpired(item)) {
      this.delete(key);
      return null;
    }

    return this.processGetResult(key, item);
  }

  private processGetResult<T>(key: string, item: CacheItem<T>): T {
    // Atualizar estatísticas de acesso
    item.accessCount++;
    item.lastAccessed = Date.now();

    console.log(`📦 Cache HIT: ${key} (Acessos: ${item.accessCount})`);

    // Descomprimir se necessário
    return this.config.enableCompression ? this.decompress(item.data) : item.data;
  }

  // Remover do cache
  delete(key: string): boolean {
    const deleted = this.memoryCache.delete(key);
    
    if (deleted && this.config.enablePersistence) {
      this.removeFromStorage(key);
      console.log(`📦 Cache DELETE: ${key}`);
    }
    
    return deleted;
  }

  // Limpar cache por tags
  invalidateByTags(tags: string[]): number {
    let invalidated = 0;
    
    for (const [key, item] of this.memoryCache.entries()) {
      if (tags.some(tag => item.tags.includes(tag))) {
        this.delete(key);
        invalidated++;
      }
    }
    
    console.log(`📦 Cache INVALIDATE: ${invalidated} itens removidos por tags: ${tags.join(', ')}`);
    return invalidated;
  }

  // Limpar cache expirado
  cleanup(): number {
    let cleaned = 0;
    
    for (const [key, item] of this.memoryCache.entries()) {
      if (this.isExpired(item)) {
        this.delete(key);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`📦 Cache CLEANUP: ${cleaned} itens expirados removidos`);
    }
    
    return cleaned;
  }

  // Limpar todo o cache
  clear(): void {
    this.memoryCache.clear();
    
    if (this.config.enablePersistence) {
      this.clearStorage();
    }
    
    console.log('📦 Cache CLEAR: Todo o cache foi limpo');
  }

  // Verificar se item expirou
  private isExpired(item: CacheItem<any>): boolean {
    return Date.now() - item.timestamp > item.ttl;
  }

  // Remover item menos usado
  private evictLeastUsed(): void {
    let leastUsedKey = '';
    let leastUsedCount = Infinity;
    let oldestAccess = Infinity;

    for (const [key, item] of this.memoryCache.entries()) {
      // Priorizar por menor uso e acesso mais antigo
      if (item.accessCount < leastUsedCount || 
          (item.accessCount === leastUsedCount && item.lastAccessed < oldestAccess)) {
        leastUsedKey = key;
        leastUsedCount = item.accessCount;
        oldestAccess = item.lastAccessed;
      }
    }

    if (leastUsedKey) {
      this.delete(leastUsedKey);
      console.log(`📦 Cache EVICT: ${leastUsedKey} (Acessos: ${leastUsedCount})`);
    }
  }

  // Processo de limpeza automática
  private startCleanupProcess(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 60000); // A cada minuto
  }

  // Parar processo de limpeza
  stopCleanupProcess(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }
  }

  // Compressão de dados
  private compress<T>(data: T): T {
    if (!this.config.enableCompression) return data;
    
    try {
      // Simula compressão (em produção, usar biblioteca como pako)
      const jsonString = JSON.stringify(data);
      if (jsonString.length > 1000) {
        // Aplicar compressão apenas para dados grandes
        return data; // Placeholder para compressão real
      }
      return data;
    } catch (error) {
      console.warn('Erro na compressão:', error);
      return data;
    }
  }

  // Descompressão de dados
  private decompress<T>(data: T): T {
    if (!this.config.enableCompression) return data;
    
    try {
      // Simula descompressão
      return data; // Placeholder para descompressão real
    } catch (error) {
      console.warn('Erro na descompressão:', error);
      return data;
    }
  }

  // Persistência no localStorage
  private persistToStorage(key: string, item: CacheItem<any>): void {
    if (!this.config.enablePersistence) return;
    
    try {
      const cacheKey = `cache_${key}`;
      const serialized = JSON.stringify({
        ...item,
        data: JSON.stringify(item.data)
      });
      localStorage.setItem(cacheKey, serialized);
    } catch (error) {
      console.warn('Erro ao persistir cache:', error);
    }
  }

  // Carregar da persistência
  private loadFromStorage(key: string): CacheItem<any> | null {
    if (!this.config.enablePersistence) return null;
    
    try {
      const cacheKey = `cache_${key}`;
      const serialized = localStorage.getItem(cacheKey);
      
      if (!serialized) return null;
      
      const parsed = JSON.parse(serialized);
      return {
        ...parsed,
        data: JSON.parse(parsed.data)
      };
    } catch (error) {
      console.warn('Erro ao carregar cache:', error);
      return null;
    }
  }

  // Remover da persistência
  private removeFromStorage(key: string): void {
    if (!this.config.enablePersistence) return;
    
    try {
      const cacheKey = `cache_${key}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Erro ao remover cache:', error);
    }
  }

  // Carregar todo o cache da persistência
  private loadFromPersistence(): void {
    if (!this.config.enablePersistence) return;
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          const originalKey = key.replace('cache_', '');
          const item = this.loadFromStorage(originalKey);
          
          if (item && !this.isExpired(item)) {
            this.memoryCache.set(originalKey, item);
          } else if (item) {
            // Remover item expirado
            this.removeFromStorage(originalKey);
          }
        }
      }
      
      console.log(`📦 Cache LOAD: ${this.memoryCache.size} itens carregados da persistência`);
    } catch (error) {
      console.warn('Erro ao carregar cache da persistência:', error);
    }
  }

  // Limpar persistência
  private clearStorage(): void {
    if (!this.config.enablePersistence) return;
    
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Erro ao limpar persistência:', error);
    }
  }

  // Estatísticas do cache
  getStats() {
    const now = Date.now();
    let totalSize = 0;
    let expiredCount = 0;
    let hitCount = 0;
    
    for (const [key, item] of this.memoryCache.entries()) {
      totalSize += JSON.stringify(item.data).length;
      if (this.isExpired(item)) expiredCount++;
      hitCount += item.accessCount;
    }

    return {
      size: this.memoryCache.size,
      maxSize: this.config.maxSize,
      totalSize,
      expiredCount,
      hitCount,
      hitRate: hitCount / Math.max(this.memoryCache.size, 1),
      memoryUsage: (totalSize / 1024 / 1024).toFixed(2) + ' MB'
    };
  }

  // Métodos de conveniência para diferentes tipos de cache
  
  // Cache de API
  cacheApiResponse<T>(endpoint: string, data: T, ttl = 5 * 60 * 1000): void {
    this.set(`api_${endpoint}`, data, {
      ttl,
      tags: ['api', 'response'],
      priority: 'normal'
    });
  }

  getApiResponse<T>(endpoint: string): T | null {
    return this.get<T>(`api_${endpoint}`);
  }

  // Cache de componentes
  cacheComponentData<T>(component: string, data: T, ttl = 10 * 60 * 1000): void {
    this.set(`component_${component}`, data, {
      ttl,
      tags: ['component', 'ui'],
      priority: 'high'
    });
  }

  getComponentData<T>(component: string): T | null {
    return this.get<T>(`component_${component}`);
  }

  // Cache de usuário
  cacheUserData<T>(userId: string, data: T, ttl = 30 * 60 * 1000): void {
    this.set(`user_${userId}`, data, {
      ttl,
      tags: ['user', 'profile'],
      priority: 'high'
    });
  }

  getUserData<T>(userId: string): T | null {
    return this.get<T>(`user_${userId}`);
  }

  // Invalidar cache relacionado
  invalidateApiCache(): number {
    return this.invalidateByTags(['api']);
  }

  invalidateComponentCache(): number {
    return this.invalidateByTags(['component']);
  }

  invalidateUserCache(userId?: string): number {
    if (userId) {
      return this.delete(`user_${userId}`) ? 1 : 0;
    }
    return this.invalidateByTags(['user']);
  }
}